import { v4 as uuidv4 } from 'uuid';

import type { JourneyFields, SignupSource } from '../../common/hooks/use-signup-api.hook';

/**
 * The visitor's journey through "Start for free", before there is an account
 * (docs/plg-review.md → Journey). One id per browser, made when `/start`
 * first opens, plus where the visitor came from on that first visit. Both go
 * with every public signup call, so the admin Signups page can show the
 * people who checked a website and never registered.
 *
 * Storage is a convenience: when it is blocked (private mode, cleared site
 * data), the ids live in memory for this page load and nothing breaks.
 */
export const JOURNEY_KEY = 'ardealy.signup.journey';
export const JOURNEY_SOURCE_KEY = 'ardealy.signup.journey.source';

/** Campaign tags, and Google's click id (ardealy.com passes them along to /start). */
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'] as const;
/** The API keeps short strings; a pasted essay in a UTM is cut. */
const MAX_LEN = 200;

let memory: { journeyId?: string; source?: SignupSource } = {};

/** The stored value; `undefined` when storage itself cannot be used (then memory stands in). */
const read = (key: string): string | null | undefined => {
    try {
        return localStorage.getItem(key);
    } catch {
        return undefined;
    }
};

const write = (key: string, value: string) => {
    try {
        localStorage.setItem(key, value);
    } catch {
        /* memory keeps it for this visit */
    }
};

const clip = (v: string | null | undefined) => {
    const s = String(v ?? '').trim();
    return s ? s.slice(0, MAX_LEN) : undefined;
};

/**
 * Where this visit came from: the `utm_*` of the `/start` URL, and the page
 * that linked here — unless that page is the app itself (a reload, or the
 * login page's "Start for free" link says nothing about the campaign).
 */
export function readSource(search: string, referrer: string, ownHost?: string): SignupSource | undefined {
    const params = new URLSearchParams(search || '');
    const source: SignupSource = {};
    for (const key of UTM_KEYS) {
        const value = clip(params.get(key));
        if (value) source[key] = value;
    }
    const ref = clip(referrer);
    if (ref) {
        let sameHost = false;
        try {
            sameHost = Boolean(ownHost) && new URL(ref).host === ownHost;
        } catch {
            /* not a URL: keep it as it came */
        }
        if (!sameHost) source.referrer = ref;
    }
    return Object.keys(source).length ? source : undefined;
}

const parseSource = (raw: string | null): SignupSource | undefined => {
    if (!raw) return undefined;
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? (parsed as SignupSource) : undefined;
    } catch {
        return undefined;
    }
};

/** The journey already under way in this browser, if any — never makes one. */
export function readJourney(): JourneyFields {
    const stored = read(JOURNEY_KEY);
    const usable = stored !== undefined;
    const journeyId = usable ? stored : memory.journeyId;
    if (!journeyId) return {};
    const source = usable ? parseSource(read(JOURNEY_SOURCE_KEY) ?? null) : memory.source;
    return source ? { journeyId, source } : { journeyId };
}

/**
 * Called when `/start` opens: the existing journey, or a new one whose source
 * is read from this (first) visit. A later visit keeps the first source — the
 * campaign that brought someone is the one that counts.
 */
export function startJourney(
    search: string,
    referrer: string = typeof document !== 'undefined' ? document.referrer : '',
    ownHost: string = typeof window !== 'undefined' ? window.location.host : '',
): JourneyFields {
    const existing = readJourney();
    if (existing.journeyId) return existing;

    const journeyId = typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function' ? (crypto as any).randomUUID() : uuidv4();
    const source = readSource(search, referrer, ownHost);
    memory = { journeyId, source };
    write(JOURNEY_KEY, journeyId);
    if (source) write(JOURNEY_SOURCE_KEY, JSON.stringify(source));
    return source ? { journeyId, source } : { journeyId };
}

/** Tests only: forget the in-memory fallback. */
export const resetJourneyMemory = () => {
    memory = {};
};
